<script setup lang="ts">
/**
 * State canônico: Onboarding
 *
 * 5 turnos (D7 / ADR-0014), cada um com copy neutra e convite (CC-05,
 * Princípio V). Usuário pode voltar (BACK) ou pular o Turn 4 (SKIP).
 *
 * HLOS-46: instrumenta duração do onboarding (markStart/markComplete)
 * para validar gate AC5-S4 (≤ 5min).
 *
 * Constitucional:
 * - Zero timer/contador visível (CC-01).
 * - Zero urgência artificial (CC-04).
 * - Copy respeitosa, sem "AI"/"assistant" (Princípio XI).
 * - Mnemonic de 24 palavras é placeholder em v0.2 — HLOS-7 ativa vault.
 */
import { computed, onMounted, ref } from "vue";
import {
  INITIAL_DRAFT,
  INITIAL_STEP,
  transition,
} from "../onboarding/rules";
import type { OnboardingDraft, OnboardingStep } from "../onboarding/types";
import { useOnboardingStore } from "./onboarding/store";
import { AIDisclosureNotice } from "../onboarding";

interface Emits {
  (e: "complete", draft: OnboardingDraft): void;
}

const emit = defineEmits<Emits>();
const onboarding = useOnboardingStore();

// Estado do onboarding (não compartilhado via Pinia — state efêmero
// de sessão, conforme R6 do Companion Runtime: nada persiste sem opt-in).
const step = ref<OnboardingStep>(INITIAL_STEP);

// HLOS-46: marca início no mount.
onMounted(() => {
  onboarding.markStart();
});
const draft = ref<OnboardingDraft>(INITIAL_DRAFT);
const startedAt = ref<number>(Date.now());

// Buffers locais de input (não persistem entre turnos até NEXT).
const nameInput = ref<string>("");
const focusInput = ref<string>("");
const personalInput = ref<string>("");
const mnemonicConfirmed = ref<boolean>(false);
// HLOS-225: gate "Entendi" no AIDisclosureNotice dentro do Turn 5 (mnemonic).
// Disclosure deve ser lida + dismissed ANTES de habilitar "Concluir".
const disclosureDismissed = ref<boolean>(false);

function onDisclosureDismiss(): void {
  disclosureDismissed.value = true;
}

const index = computed<number>(() => {
  const i = ["intro", "name", "focus", "personal", "mnemonic"].indexOf(step.value);
  return i < 0 ? 0 : i;
});

const totalSteps = 5;
const isFirst = computed<boolean>(() => step.value === "intro");

function commitNext(): void {
  // Tipo mutável local (TS Partial<T> preserva readonly em TS 5.x).
  interface WritableDraft {
    name?: string | null;
    focus?: string | null;
    personal?: string | null;
    mnemonicConfirmed?: boolean;
  }
  const partial: WritableDraft = {};
  if (step.value === "name") partial.name = nameInput.value.trim() || null;
  if (step.value === "focus") partial.focus = focusInput.value.trim() || null;
  if (step.value === "personal") partial.personal = personalInput.value.trim() || null;
  if (step.value === "mnemonic") partial.mnemonicConfirmed = mnemonicConfirmed.value;

  const result = transition(step.value, draft.value, startedAt.value, {
    type: "NEXT",
    draft: partial,
  });

  step.value = (result.nextStep === "done" ? "mnemonic" : result.nextStep) as OnboardingStep;
  draft.value = result.nextDraft;

  if (result.nextStep === "done") {
    emit("complete", result.nextDraft);
  }
}

function goBack(): void {
  const result = transition(step.value, draft.value, startedAt.value, { type: "BACK" });
  if (result.nextStep !== "done") {
    step.value = result.nextStep;
    draft.value = result.nextDraft;
  }
}

function skipPersonal(): void {
  const result = transition(step.value, draft.value, startedAt.value, { type: "SKIP" });
  if (result.nextStep !== step.value) {
    step.value = result.nextStep as OnboardingStep;
    draft.value = result.nextDraft;
  }
}

function finishOnboarding(): void {
  const result = transition(step.value, draft.value, startedAt.value, { type: "FINISH" });
  if (result.nextStep === "done") {
    // HLOS-46: marca conclusão antes de emitir; app pai pode ler durationMs.
    onboarding.markComplete();
    const duration = onboarding.durationMs;
    if (duration !== null) {
      console.info(`[onboarding] completed in ${duration}ms`);
    }
    emit("complete", result.nextDraft);
  }
}
</script>

<template>
  <section
    class="hlos-onboarding"
    data-testid="onboarding-state"
    :data-step="step"
  >
    <!--
      Indicador textual simples ("Passo 2 de 5") sem cor/animação.
      Não é contador de progresso manipulativo (CC-04): apenas orientação.
    -->
    <p class="hlos-onboarding__step-indicator" aria-live="polite">
      Passo {{ index + 1 }} de {{ totalSteps }}
    </p>

    <!-- Turn 1: Apresentação -->
    <article v-if="step === 'intro'" data-testid="onboarding-intro">
      <h2>Vamos começar</h2>
      <p>
        Eu sou seu Companheiro Intelectual de longo prazo. Posso te ajudar a
        compreender melhor o que você estuda — no seu ritmo, com seu histórico,
        sem pressa.
      </p>
      <p>
        Antes de começarmos, vou fazer cinco perguntas curtas. Você pode levar
        o tempo que quiser; nada aqui é automático.
      </p>
      <button type="button" data-testid="onboarding-intro-continue" @click="commitNext">
        Começar
      </button>
    </article>

    <!-- Turn 2: Nome -->
    <article v-else-if="step === 'name'" data-testid="onboarding-name">
      <h2>Como você quer ser chamado?</h2>
      <label for="hlos-onboarding-name">Seu nome ou apelido</label>
      <input
        id="hlos-onboarding-name"
        v-model="nameInput"
        type="text"
        placeholder="(opcional)"
        autocomplete="off"
        data-testid="onboarding-name-input"
      />
      <small>Você pode deixar em branco se preferir.</small>
      <div class="hlos-onboarding__actions">
        <button type="button" data-testid="onboarding-back" :disabled="isFirst" @click="goBack">
          Voltar
        </button>
        <button type="button" data-testid="onboarding-next" @click="commitNext">
          Continuar
        </button>
      </div>
    </article>

    <!-- Turn 3: Foco de compreensão -->
    <article v-else-if="step === 'focus'" data-testid="onboarding-focus">
      <h2>O que você quer compreender melhor?</h2>
      <label for="hlos-onboarding-focus">Tema, área ou pergunta</label>
      <textarea
        id="hlos-onboarding-focus"
        v-model="focusInput"
        rows="3"
        placeholder="Ex.: 'Quero entender melhor como funções compostas funcionam.'"
        data-testid="onboarding-focus-input"
      />
      <small>Pode ser uma área ampla ou uma pergunta específica.</small>
      <div class="hlos-onboarding__actions">
        <button type="button" data-testid="onboarding-back" @click="goBack">
          Voltar
        </button>
        <button type="button" data-testid="onboarding-next" @click="commitNext">
          Continuar
        </button>
      </div>
    </article>

    <!-- Turn 4: Contexto pessoal (pode pular) -->
    <article v-else-if="step === 'personal'" data-testid="onboarding-personal">
      <h2>Tem algo que eu deveria saber sobre você?</h2>
      <label for="hlos-onboarding-personal">Contexto opcional</label>
      <textarea
        id="hlos-onboarding-personal"
        v-model="personalInput"
        rows="3"
        placeholder="(opcional — você pode pular este turno)"
        data-testid="onboarding-personal-input"
      />
      <small>Nada disso sai do seu dispositivo.</small>
      <div class="hlos-onboarding__actions">
        <button type="button" data-testid="onboarding-back" @click="goBack">
          Voltar
        </button>
        <button type="button" data-testid="onboarding-skip" @click="skipPersonal">
          Pular este turno
        </button>
        <button type="button" data-testid="onboarding-next" @click="commitNext">
          Continuar
        </button>
      </div>
    </article>

    <!-- Turn 5: Mnemonic (placeholder até HLOS-7 ativar vault) -->
    <article v-else-if="step === 'mnemonic'" data-testid="onboarding-mnemonic">
      <h2>Anote estas 24 palavras</h2>
      <p>
        Quando o cofre local for ativado, vamos te apresentar 24 palavras em
        uma única tela, sem pressa. Você pode anotar em papel e guardar em
        lugar seguro — é a única forma de recuperar o que registramos juntos.
      </p>
      <p>
        Por enquanto, esta etapa é apenas um lembrete. A geração das 24
        palavras acontece em uma fase posterior, quando o cofre local
        estiver pronto.
      </p>
      <label>
        <input v-model="mnemonicConfirmed" type="checkbox" data-testid="onboarding-mnemonic-check" />
        Compreendi — quero continuar sem gerar as palavras agora.
      </label>

      <!-- HLOS-225: Disclosure UX (Princípio XI — Companion ≠ LLM).
           Mostra como o Companheiro responde, sem citar provider LLM.
           Gate: usuário deve ler e "Entendi" antes de habilitar "Concluir". -->
      <AIDisclosureNotice
        scope="onboarding"
        :dismissible="!disclosureDismissed"
        data-testid="onboarding-disclosure"
        @dismiss="onDisclosureDismiss"
      />

      <div class="hlos-onboarding__actions">
        <button type="button" data-testid="onboarding-back" @click="goBack">
          Voltar
        </button>
        <button
          type="button"
          data-testid="onboarding-finish"
          :disabled="!mnemonicConfirmed || !disclosureDismissed"
          @click="finishOnboarding"
        >
          Concluir
        </button>
      </div>
    </article>

    <footer class="hlos-onboarding__footer">
      <!--
        PrivacyFooter removido daqui em HLOS-41 (HLOS-37.4).
        Agora vive no nível raiz em App.vue (zero CLS entre estados).
      -->
    </footer>
  </section>
</template>

<style scoped>
.hlos-onboarding {
  max-width: 640px;
  margin: 0 auto;
  padding: 1.5rem;
}
.hlos-onboarding__step-indicator {
  opacity: 0.6;
  font-size: 0.85em;
  margin: 0 0 1rem 0;
}
article {
  margin-bottom: 1.5rem;
}
h2 {
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
}
label {
  display: block;
  margin-top: 0.75rem;
  font-size: 0.95em;
}
input[type="text"],
textarea {
  width: 100%;
  font: inherit;
  padding: 0.5rem;
  margin-top: 0.25rem;
  border: 1px solid color-mix(in srgb, currentColor 25%, transparent);
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  resize: vertical;
}
small {
  display: block;
  margin-top: 0.25rem;
  opacity: 0.7;
  font-size: 0.85em;
}
.hlos-onboarding__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}
button {
  font: inherit;
  padding: 0.5rem 1rem;
  border: 1px solid currentColor;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
button:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 10%, transparent);
}
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.hlos-onboarding__footer {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  opacity: 0.7;
}
</style>
